// Pure read-only hook — returns where THIS user should land based on
// platform-admin mode, role default, org default, entitlements, and
// permissions. No navigation, no setState, no effects. Safe to call
// from anywhere in the authenticated tree.
//
// AuthRedirect consumes the result and decides when to navigate.
// Header consumes the result and assigns it to the title link's href.
// Same fallback chain as the existing AuthRedirect logic — extracted
// here so both surfaces give consistent answers (the title click
// previously hardcoded `/chat`, which 401'd when chatbot was off).

import { useMemo } from "react";
import { useAuth } from "./useAuth";
import { useModules } from "../context/ModulesContext";

export interface DefaultLandingResult {
  // Resolved destination route, or null when the answer isn't computable
  // yet (auth + entitlements + modules + roles all need to be settled).
  route: string | null;
  // Mirrors `route !== null` — exposed separately for call sites that
  // want to gate effects on readiness rather than the route value itself.
  ready: boolean;
}

const NOT_READY: DefaultLandingResult = { route: null, ready: false };

export function useDefaultLandingRoute(): DefaultLandingResult {
  const { isAuthenticated, isPlatformAdminMode, entitlements, roles } =
    useAuth();
  const { modules, ready: modulesReady } = useModules();

  return useMemo<DefaultLandingResult>(() => {
    // Pre-auth or auth still loading → nothing to suggest.
    if (!isAuthenticated) return NOT_READY;

    // Platform admins always land on the SuperAdmin dashboard. This
    // resolves first because it's independent of org-level state.
    if (isPlatformAdminMode === true) {
      return { route: "/superadmin/dashboard", ready: true };
    }

    // Wait for all org-scoped state to be present. Returning NOT_READY
    // is the safe path — Header falls back to "/" and AuthRedirect
    // simply doesn't navigate yet.
    if (
      isPlatformAdminMode === null ||
      !entitlements ||
      !roles.permissions ||
      roles.permissions.length === 0 ||
      !modulesReady
    ) {
      return NOT_READY;
    }

    const userModules = modules
      .filter((m) => m.route && m.viewPermission)
      .sort((a, b) => a.displayOrder - b.displayOrder);

    // Three-tier fallback (mirrors the existing AuthRedirect chain):
    //   1. role default → 2. org default → 3. "chatbot"
    const defaultKey =
      roles.defaultModule || entitlements?.defaultModule || "chatbot";
    const defaultMod = userModules.find((m) => m.key === defaultKey);

    if (defaultMod && roles.permissions.includes(defaultMod.viewPermission!)) {
      return { route: defaultMod.route!, ready: true };
    }

    // Final fallback: first user-facing module the user is BOTH entitled
    // to and permitted to view. /unauthorized only when even that fails.
    const fallback = userModules.find((m) => {
      const entitled =
        m.entitlementKey === null ||
        entitlements?.features?.[m.entitlementKey]?.enabled === true;
      return entitled && roles.permissions.includes(m.viewPermission!);
    });

    return { route: fallback?.route || "/unauthorized", ready: true };
  }, [isAuthenticated, isPlatformAdminMode, entitlements, roles, modules, modulesReady]);
}
